import React, { useEffect } from "react";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { useTranslation } from "@/translations/TranslationContext";
import logo from "@/assets/logo.png";
import ReliableAccounting from "@/assets/reliableaccounting.png";
import { FaLinkedin, FaFacebook } from 'react-icons/fa';

import "./Footer.css";

function Footer() {
  const { t } = useTranslation();
  const controls = useAnimation();
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true
  });

  useEffect(() => {
    if (inView) {
      controls.start("visible");
    }
  }, [controls, inView]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5 }
    }
  };

  const imageVariants = {
    hidden: { x: 50, opacity: 0 },
    visible: {
      x: 0,
      opacity: 1,
      transition: { duration: 0.7, ease: "easeOut" }
    }
  };

  const highlightStyle = {
    color: "#1B9DB8",
    fontWeight: "bold"
  };

  return (
    <>
      <motion.div 
        className="container bottombluebox"
        ref={ref}
        initial="hidden"
        animate={controls}
        variants={containerVariants}
      >
        <motion.div variants={containerVariants}>
          <motion.h2 variants={itemVariants}>
            <span style={highlightStyle}>{t.footer.reliable.title.highlight1}</span>
            {t.footer.reliable.title.part2}
          </motion.h2>
          <motion.p variants={itemVariants}>
            {t.footer.reliable.description}
          </motion.p>
          {/* No animations for the button */}
          <a 
            className="btn btn-secondary" 
            href="/contact"
            style={{
              backgroundColor: '#0C4652',
              color: '#fff',
              borderRadius: '30px',
              padding: '12px 24px',
              textTransform: 'none',
              fontFamily: '"Montserrat", sans-serif',
              fontWeight: 600,
              display: 'inline-block',
              textDecoration: 'none',
              border: 'none',
              cursor: 'pointer',
              transition: 'background-color 0.3s ease'
            }}
          >
            {t.footer.reliable.button}
          </a>
        </motion.div>
        <motion.div variants={imageVariants}>
          <motion.img 
            src={ReliableAccounting} 
            alt="ReliableAccounting"
            whileHover={{ scale: 1.05, transition: { duration: 0.3 } }}
          />
        </motion.div>
      </motion.div>
      <div className="footer">
        <div className="container">
          <footer className="row row-cols-1 row-cols-md-2">
            <div className="col col-md-5 bottomlogo">
              <motion.a
                href="/"
                className="d-flex align-items-center mb-3 link-body-emphasis text-decoration-none"
                aria-label="Bootstrap"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                whileHover={{ scale: 1.05, transition: { duration: 0.2 } }}
              >
                <img src={logo} alt="Logo" />
              </motion.a>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.5 }}
              >
                {t.footer.about}
              </motion.p>
              <address style={{ marginTop: '10px', fontStyle: 'normal' }}>
                {t.contact.info.location.value}
              </address>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.5 }}
                style={{ marginTop: '10px' }}
              >
                <a 
                  href="https://www.linkedin.com/company/algebra-solution-financiere/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  aria-label="LinkedIn"
                  style={{
                    display: 'inline-block',
                    color: '#0A66C2',
                    fontSize: '24px',
                    marginRight: '15px',
                    transition: 'transform 0.3s ease'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.2)'}
                  onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                >
                  <FaLinkedin />
                </a>
                <a 
                  href="https://www.facebook.com/profile.php?id=61576662205885" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                  style={{
                    display: 'inline-block',
                    color: '#1877F2',
                    fontSize: '24px',
                    transition: 'transform 0.3s ease'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.2)'}
                  onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                >
                  <FaFacebook />
                </a>
              </motion.div>
            </div>

            <div className="col col-md-7 bottomnavi">
              <div className="row row-cols-1 row-cols-md-3">
                <div className="col">
                  <motion.h5
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3, duration: 0.5 }}
                  >
                    {t.footer.services.title}
                  </motion.h5>
                  <ul className="nav flex-column">
                    <motion.li 
                      className="nav-item"
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.4, duration: 0.5 }}
                    >
                      <a href="/tax-preparation" className="nav-link p-0">
                        {t.footer.services.taxPlanning}
                      </a>
                    </motion.li>
                    <motion.li 
                      className="nav-item"
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.5, duration: 0.5 }}
                    >
                      <a href="/financial-planning" className="nav-link p-0">
                        {t.footer.services.financialAnalysis}
                      </a>
                    </motion.li>
                    <motion.li 
                      className="nav-item"
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.6, duration: 0.5 }}
                    >
                      <a href="/monthly-bookkeeping" className="nav-link p-0">
                        {t.footer.services.bookkeepingServices}
                      </a>
                    </motion.li>
                    <motion.li 
                      className="nav-item"
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.7, duration: 0.5 }}
                    >
                      <a href="/business-consulting" className="nav-link p-0">
                        {t.footer.services.businessConsulting}
                      </a>
                    </motion.li>
                  </ul>
                </div>

                <div className="col mb-3">
                  <motion.h5
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3, duration: 0.5 }}
                  >
                    {t.footer.company.title}
                  </motion.h5>
                  <ul className="nav flex-column">
                    <motion.li 
                      className="nav-item"
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.4, duration: 0.5 }}
                    >
                      <a href="/services" className="nav-link p-0">
                        {t.footer.company.services}
                      </a>
                    </motion.li>
                    <motion.li 
                      className="nav-item"
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.5, duration: 0.5 }}
                    >
                      <a href="/about" className="nav-link p-0">
                        {t.footer.company.aboutUs}
                      </a>
                    </motion.li>
                  </ul>
                </div>

                <div className="col mb-3">
                  <motion.h5
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3, duration: 0.5 }}
                  >
                    {t.footer.contact.title}
                  </motion.h5>
                  <ul className="nav flex-column">
                    <motion.li 
                      className="nav-item"
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.4, duration: 0.5 }}
                    >
                      <a href="tel:+15145085552" className="nav-link p-0">
                        (514) 508-5552
                      </a>
                    </motion.li>
                    <motion.li 
                      className="nav-item"
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.5, duration: 0.5 }}
                    >
                      <a href="mailto:info@algebrasolution.ca" className="nav-link p-0">
                        info@algebrasolution.ca
                      </a>
                    </motion.li>
                    <motion.li 
                      className="nav-item"
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.6, duration: 0.5 }}
                    >
                      <a href="/contact" className="nav-link p-0">
                        {t.footer.contact.contactUs}
                      </a>
                    </motion.li>
                  </ul>
                </div>
              </div>
            </div>
          </footer>
        </div>
        <hr />
        <div className="container">
          <motion.p 
            className="text-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8, duration: 0.5 }}
          >
            {t.footer.copyright}
          </motion.p>
        </div>
      </div>
    </>
  );
}

export default Footer;
